import i18n from "../i18n";

/**
 * The harness's failure messages and step inputs, read back as sentences.
 *
 * THE GRAMMAR IS THE HARNESS'S, not a guess at it. `harness/engine.py`
 * `_check()` writes one of a dozen assertion shapes; the runner wraps the
 * first failing one as `step N (['action']): <assertion>`; pytest prefixes
 * `AssertionError: `; and `actions_platform.py` reports whatever `wait_for`
 * last saw as `condition not met after Ts (K attempts): <why>` behind a
 * synthetic `rc 1 not in [0]`. A message that matches none of them is shown
 * as it came, minus the noise below - a reading that guesses is worse than
 * the raw text, because it looks sure.
 *
 * Two things are removed before anything is shown. ssh's "Permanently added
 * ... to the list of known hosts", which the bench's throwaway known-hosts
 * file prints on EVERY connection: it was the entire stderr of 56 of the 401
 * failures on record (2026-09-10), so the page quoted a banner where it
 * should have said "no error message". And the password after `sshpass -p`:
 * five messages on record carried the HMI's N3 password verbatim, to anyone
 * who could read the run.
 */

export type Reason =
  | { kind: "rc"; rc: string; want: string; stderr: string; truncated: boolean }
  | { kind: "timeout"; secs: string; attempts: number; inner: Reason | null; truncated?: boolean }
  | { kind: "ne"; path: string; got: string; want: string }
  | { kind: "unchanged"; path: string; got: string }
  | { kind: "notContains"; path: string; got: string; want: string }
  | { kind: "noMatch"; path: string; got: string; pattern: string }
  | { kind: "cmp"; path: string; got: string; op: string; want: string }
  | { kind: "range"; path: string; got: string; lo: string; hi: string }
  | { kind: "missing"; path: string }
  | { kind: "stdoutLacks"; want: string }
  | { kind: "stdoutHas"; want: string }
  | { kind: "stdoutNe"; got: string; want: string }
  | { kind: "stdoutSame"; got: string }
  | { kind: "httpTimeout"; host: string; port: string; secs: string }
  | { kind: "httpDropped" }
  | { kind: "notJson" }
  | { kind: "cmdTimeout"; cmd: string; secs: string }
  | { kind: "cmdFailed"; rc: string; cmd: string; stderr: string }
  | { kind: "exception"; name: string; text: string }
  | { kind: "text"; text: string };

export interface Failure {
  step?: number;
  action?: string;
  reason: Reason;
  /** What the harness wrote, password masked: the "raw message" disclosure. */
  raw: string;
}

const SSH_NOTICE =
  /Warning: Permanently added '[^']*' \([^)]*\) to the list of known hosts\.(?:\r?\n|\\n)?/g;

export function maskSecrets(s: string): string {
  return (
    s
      // As subprocess prints a command: 'sshpass', '-p', 'SECRET'
      .replace(/('sshpass',\s*'-p',\s*')([^']*)(')/g, "$1***$3")
      // As a shell line: sshpass -p SECRET, quoted or not
      .replace(/(sshpass\s+-p\s+)('[^']*'|"[^"]*"|\S+)/g, "$1***")
  );
}

export function stripNoise(s: string): string {
  return s.replace(SSH_NOTICE, "");
}

/**
 * One Python repr, read back.
 *
 * The harness formats stderr as `{stderr!r:.200}`, so a long one arrives CUT,
 * without its closing quote - expected, not an error, and reported as such.
 */
export function unrepr(s: string): { text: string; truncated: boolean } {
  const q = s[0];
  if (q !== "'" && q !== '"') return { text: s, truncated: false };
  let closed = false;
  if (s.length >= 2 && s.endsWith(q)) {
    let slashes = 0;
    for (let i = s.length - 2; i >= 1 && s[i] === "\\"; i--) slashes++;
    closed = slashes % 2 === 0;
  }
  const body = closed ? s.slice(1, -1) : s.slice(1);
  const text = body.replace(/\\(n|t|r|'|"|\\)/g, (_, c: string) =>
    c === "n" ? "\n" : c === "t" ? "\t" : c === "r" ? "\r" : c,
  );
  return { text, truncated: !closed };
}

// A dotted JSON path as `_check` prints it: `modem.status`, `items.0.name`.
const PATH = String.raw`([\w.\-\[\]$]+)`;
const re = (body: string) => new RegExp(`^${body}$`);
const R_UNCHANGED = re(`${PATH}=([\\s\\S]*) unchanged \\(expected != [\\s\\S]*\\)`);
const R_NOTCONTAINS = re(`${PATH}=([\\s\\S]*) !contains ([\\s\\S]*)`);
const R_NOMATCH = re(`${PATH}=([\\s\\S]*) !~ \\/([\\s\\S]*)\\/`);
const R_RANGE = re(`${PATH}=(\\S+) not in \\[([^,\\]]+), ([^\\]]+)\\]`);
const R_CMP = re(`${PATH}=([\\s\\S]*) not (>=|<=|>|<) ([\\s\\S]*)`);
const R_NE = re(`${PATH}=([\\s\\S]*?) != ([\\s\\S]*)`);
const R_MISSING = new RegExp(`^${PATH} not present in `);
const SYMBOL: Record<string, string> = { ">=": "≥", "<=": "≤", ">": ">", "<": "<" };

/** One assertion message - the part after `step N (['action']): `. */
export function parseReason(input: string): Reason {
  const s = input.trim();
  let m: RegExpMatchArray | null;

  // The one shape that nests another message, and the one wait_for wraps.
  if ((m = s.match(/^rc (-?\d+) not in \[([^\]]*)\] \(stderr=([\s\S]*)\)$/))) {
    const { text, truncated } = unrepr(m[3]);
    const stderr = stripNoise(text).trim();
    // wait_for reports as "rc 1" around what it actually saw; the 1 is its own.
    if (stderr.startsWith("condition not met after ")) {
      const r = parseReason(stderr);
      return r.kind === "timeout" && truncated ? { ...r, truncated } : r;
    }
    return { kind: "rc", rc: m[1], want: m[2], stderr, truncated };
  }
  if ((m = s.match(/^condition not met after (\d+)s \((\d+) attempts?\)(?:: ([\s\S]*))?$/))) {
    const inner = (m[3] ?? "").trim();
    return {
      kind: "timeout",
      secs: m[1],
      attempts: Number(m[2]),
      inner: inner ? parseReason(inner) : null,
    };
  }
  if ((m = s.match(/^stdout !contains ([\s\S]+)$/))) return { kind: "stdoutLacks", want: m[1] };
  if ((m = s.match(/^stdout contains ([\s\S]+)$/))) return { kind: "stdoutHas", want: m[1] };
  if ((m = s.match(/^stdout is ([\s\S]+), which is what it must not be$/)))
    return { kind: "stdoutSame", got: m[1] };
  if ((m = s.match(/^stdout ('[\s\S]*'|"[\s\S]*") != ([\s\S]+)$/)))
    return { kind: "stdoutNe", got: m[1], want: m[2] };
  if ((m = s.match(R_UNCHANGED))) return { kind: "unchanged", path: m[1], got: m[2] };
  if ((m = s.match(R_NOTCONTAINS)))
    return { kind: "notContains", path: m[1], got: m[2], want: m[3] };
  if ((m = s.match(R_NOMATCH))) return { kind: "noMatch", path: m[1], got: m[2], pattern: m[3] };
  if ((m = s.match(R_RANGE)))
    return { kind: "range", path: m[1], got: m[2], lo: m[3].trim(), hi: m[4].trim() };
  if ((m = s.match(R_CMP)))
    return { kind: "cmp", path: m[1], got: m[2], op: SYMBOL[m[3]] ?? m[3], want: m[4] };
  if ((m = s.match(R_NE))) return { kind: "ne", path: m[1], got: m[2], want: m[3] };
  if ((m = s.match(R_MISSING))) return { kind: "missing", path: m[1] };

  // Transport and harness errors, which arrive as `module.Name: text`.
  if (
    (m = s.match(
      /ReadTimeout: HTTPConnectionPool\(host='([^']+)', port=(\d+)\): Read timed out\. \(read timeout=([\d.]+)\)/,
    ))
  )
    return { kind: "httpTimeout", host: m[1], port: m[2], secs: m[3] };
  if (/RemoteDisconnected\(/.test(s)) return { kind: "httpDropped" };
  if (/JSONDecodeError: Expecting value: line 1 column 1 \(char 0\)/.test(s))
    return { kind: "notJson" };
  if ((m = s.match(/TimeoutExpired: Command '\[([\s\S]*)\]' timed out after ([\d.]+) seconds$/))) {
    // The last argument is what was asked of the unit; the rest is ssh's.
    const args = m[1].split(/',\s*'/);
    const cmd = (args[args.length - 1] ?? "").replace(/^'|'$/g, "");
    return { kind: "cmdTimeout", cmd, secs: m[2] };
  }
  if ((m = s.match(/^cmd failed \((-?\d+)\): ([^\n]*)\nstdout=[\s\S]*?\nstderr=([\s\S]*)$/)))
    return { kind: "cmdFailed", rc: m[1], cmd: m[2], stderr: stripNoise(m[3]).trim() };
  if (
    (m = s.match(/^(?:[a-z_]\w*\.)+([A-Z]\w*): ([\s\S]*)$/)) ||
    (m = s.match(/^([A-Z]\w*(?:Error|Exception|Timeout|Expired)): ([\s\S]*)$/))
  )
    return { kind: "exception", name: m[1], text: stripNoise(m[2]).trim() };
  return { kind: "text", text: stripNoise(s).trim() };
}

/** A scenario's `message`: which step, which action, and why. */
export function parseFailure(message: string): Failure {
  const raw = maskSecrets(message.trim());
  const body = raw.replace(/^AssertionError: /, "");
  const m = body.match(/^step (\d+) \(([^)]*)\): ([\s\S]*)$/);
  if (m) {
    const action = m[2].match(/'([^']+)'/)?.[1] ?? m[2];
    return { step: Number(m[1]), action, reason: parseReason(m[3]), raw };
  }
  return { reason: parseReason(body), raw };
}

/** Long values would widen a row on a phone; the whole of it stays in `raw`. */
export function clip(s: string, max = 120): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** A command as one line: its first line, and how many follow. */
export function firstLine(cmd: string): string {
  const lines = cmd.split("\n").filter((l) => l.trim() !== "");
  if (lines.length <= 1) return cmd.trim();
  return `${lines[0].trim()} ${i18n.t("human.moreLines", { count: lines.length - 1 })}`;
}

function value(v: unknown): string {
  if (v === null) return "null";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return `[${v.map(value).join(", ")}]`;
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    const keys = Object.keys(o);
    if (keys.length === 1 && keys[0] === "name") return value(o.name);
    return keys.map((k) => `${k}: ${value(o[k])}`).join(", ");
  }
  return String(v);
}

/**
 * One assertion from a scenario, as a short phrase: `num_lock ≠ $before`,
 * `HTTP 204`, `output contains X`. Keys and meanings are `_check`'s. Anything
 * it does not know is shown as its keys and values rather than as JSON.
 */
export function formatAssert(a: unknown): string {
  const t = i18n.t.bind(i18n);
  if (a === null || a === undefined) return "";
  if (typeof a !== "object" || Array.isArray(a)) return value(a);
  const o = a as Record<string, unknown>;
  const p = o.json ?? o.response;
  if (typeof p === "string") {
    if ("equals" in o) return `${p} = ${value(o.equals)}`;
    if ("differs" in o) return `${p} ≠ ${value(o.differs)}`;
    if ("contains" in o) return t("human.a.contains", { p, v: value(o.contains) });
    if ("matches" in o) return `${p} ~ /${value(o.matches)}/`;
    if ("int_between" in o && Array.isArray(o.int_between))
      return `${p} ∈ [${o.int_between.map(value).join(", ")}]`;
    for (const [k, sym] of [["gt", ">"], ["gte", "≥"], ["lt", "<"], ["lte", "≤"]] as const)
      if (k in o) return `${p} ${sym} ${value(o[k])}`;
    if ("truthy" in o) return t(o.truthy ? "human.a.truthy" : "human.a.falsy", { p });
    if ("exists" in o) return t(o.exists ? "human.a.exists" : "human.a.absent", { p });
    return p;
  }
  if ("rc" in o) return t("human.a.rc", { v: Array.isArray(o.rc) ? o.rc.join("/") : value(o.rc) });
  if ("status" in o) return t("human.a.status", { v: value(o.status) });
  if ("stdout_contains" in o) return t("human.a.stdoutContains", { v: value(o.stdout_contains) });
  if ("stdout_not_contains" in o)
    return t("human.a.stdoutLacks", { v: value(o.stdout_not_contains) });
  if ("stdout_equals" in o) return `${t("human.output")} = ${value(o.stdout_equals)}`;
  if ("stdout_not_equals" in o) return `${t("human.output")} ≠ ${value(o.stdout_not_equals)}`;
  if ("stdout_not_blank" in o) return t("human.a.stdoutNotBlank");
  if ("not_blank" in o) return t("human.a.notBlank");
  if ("png_md5_differs" in o) return t("human.a.frameDiffers", { v: value(o.png_md5_differs) });
  if ("dimensions" in o && Array.isArray(o.dimensions) && o.dimensions.length === 2)
    return t("human.a.size", { w: value(o.dimensions[0]), h: value(o.dimensions[1]) });
  return Object.entries(o)
    .map(([k, v]) => `${k} ${value(v)}`)
    .join(", ");
}

const TIMING = new Set(["timeout", "interval", "down_timeout"]);

/**
 * What a step was asked to do, on one line, without the JSON: `POST
 * /api/x · N2`, `CTRL+I`, `kvm_key NUMLOCK · until num_lock ≠ $before · up to
 * 12 s, every 1 s`. `inner` is the step's input unwrapped from its single-key
 * envelope, as the row has always done.
 */
export function formatInput(action: string | null | undefined, inner: unknown): string {
  const t = i18n.t.bind(i18n);
  if (inner === null || inner === undefined) return "";
  if (typeof inner === "string") return firstLine(inner);
  if (typeof inner === "number")
    return action === "sleep" ? t("human.seconds", { v: inner }) : String(inner);
  if (Array.isArray(inner) || typeof inner !== "object") return value(inner);

  const o = inner as Record<string, unknown>;
  if (typeof o.method === "string" && typeof o.path === "string") {
    const parts = [`${o.method} ${o.path}`];
    if (o.level) parts.push(value(o.level));
    if (o.json !== undefined) parts.push(value(o.json));
    return parts.join(" · ");
  }
  const keys = Object.keys(o);
  if (typeof o.name === "string" && keys.every((k) => k === "name" || k === "mods")) {
    const mods = Array.isArray(o.mods) ? o.mods.map(value) : [];
    return [...mods, o.name].join("+");
  }
  if (typeof o.cmd === "string") return firstLine(o.cmd);

  const parts: string[] = [];
  if (o.sudo === true) parts.push("sudo");
  for (const [k, v] of Object.entries(o)) {
    if (k === "assert" || k === "sudo" || TIMING.has(k)) continue;
    const said = typeof v === "object" && v !== null ? formatInput(k, v) : value(v);
    parts.push(said ? `${k} ${said}` : k);
  }
  if (Array.isArray(o.assert) && o.assert.length)
    parts.push(
      t("human.until", {
        what: o.assert.map(formatAssert).join(` ${t("human.and")} `),
      }),
    );
  const timing: string[] = [];
  if (o.timeout != null) timing.push(t("human.within", { secs: value(o.timeout) }));
  if (o.interval != null) timing.push(t("human.every", { secs: value(o.interval) }));
  if (o.down_timeout != null) timing.push(`down_timeout ${t("human.seconds", { v: value(o.down_timeout) })}`);
  if (timing.length) parts.push(timing.join(", "));
  return parts.join(" · ");
}
